import { useEffect, useState } from 'react';
import { axiosClient, useAuth } from '../../auth-lib';
import { Button, Card, Input, Select, useToast } from '../../design-system';
import {
  PLANS, formatPrice, isSelfServe, planById, planForSeats, planName, rebaseSeatsForPlan,
  seatRangeLabel,
} from '../../lib/plans';
import './PlanChooser.css';

// ─── The plans, and asking for one ──────────────────────────────────────────
//
// ONE implementation, rendered by two screens for two different audiences:
//
//   • pages/SubscriptionOnboarding.jsx — a firm with no approved plan. Outside
//     the cockpit entirely, like Login and Register: no sidebar full of sections
//     they cannot open, no "Search Clients" box for a firm with no clients.
//   • the Settings → About → Subscription panel — an ACTIVE firm changing plan.
//
// ⚠️ Shared rather than duplicated, and that is the whole point. There were
// briefly two copies of this UI and they were already drifting; the fix is one
// component with two hosts, not one screen doing two jobs. A single screen was
// worse still — making a Settings route reachable by an unapproved firm needed
// two gate exemptions, and the sidebar it rendered was a list of dead ends.

// Requesting a plan commits the firm to it, so it is the Admin's call — matches
// the server rule in subscriptionController#requestSubscription (403 otherwise).
const ADMIN_LEVEL = 5;

export function PlanChooser({ subscription, seats, onChanged }) {
  const { user } = useAuth();
  const toast = useToast();

  const [selected, setSelected] = useState(null);
  const [requestedSeats, setRequestedSeats] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const isAdmin = (user?.role?.level ?? 0) >= ADMIN_LEVEL;
  const open = subscription?.openRequest || null;
  const isActive = !!subscription?.isActive;

  // Start on the plan already held, the one asked for, or — for a firm holding
  // nothing at all — Free, in that order of authority. Free is the default for a
  // brand-new firm because it is the only option that opens the platform in one
  // click; steering a firm of one at the paid band its headcount happens to fall
  // in would put a wait for approval in front of the very first screen.
  useEffect(() => {
    if (!subscription || selected) return;
    const start =
      open?.plan || subscription.plan || (isActive ? planForSeats(seats?.used || 1).id : 'free');
    setSelected(start);
    setRequestedSeats(String(planById(start)?.minSeats ?? 1));
  }, [subscription, seats, open, selected, isActive]);

  // Picking a plan re-bases the seat figure when the two disagree — see
  // lib/plans.js#rebaseSeatsForPlan for the bug this closes.
  const pickPlan = (id) => {
    setSelected(id);
    setRequestedSeats((prev) => rebaseSeatsForPlan(prev, id));
  };

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await axiosClient.post('/subscription/request', {
        plan: selected,
        requestedSeats: requestedSeats ? Number(requestedSeats) : undefined,
        note,
      });
      // ⚠️ The SERVER says whether it was granted, not this component's reading
      // of which plan was picked. Free is self-serve only from a standing where
      // nothing is in force — an active or suspended firm asking for it still
      // goes to the queue — and that condition lives in utils/subscription.js.
      // Telling a firm "you're on the free plan" because the id said 'free'
      // would be wrong exactly when it matters.
      toast.success(
        data?.granted
          ? 'You are on the free plan — your firm is open.'
          : 'Request sent. A platform administrator will review it.'
      );
      setNote('');
      await onChanged?.();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not send the request');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await axiosClient.post('/subscription/withdraw');
      toast.success('Request withdrawn');
      await onChanged?.();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not withdraw the request');
    } finally {
      setBusy(false);
    }
  };

  if (!subscription) return null;

  const suggested = requestedSeats ? planForSeats(requestedSeats).id : null;
  const pickable = isAdmin && !open;

  return (
    <>
      {/* A request already in flight REPLACES the form: the server refuses a
          second open one, so offering the form again is a 409 waiting to happen.
          Withdrawing is how you change your mind. */}
      {open && (
        <Card className="pc-pending">
          <Card.Header
            title="Awaiting approval"
            subtitle="A platform administrator is reviewing your request."
          />
          <div className="pc-pending-row">
            <span>
              You asked for the <strong>{planName(open.plan)}</strong> plan
              {open.requestedSeats ? ` for ${open.requestedSeats} users` : ''} on{' '}
              {new Date(open.requestedAt).toLocaleDateString()}
              {open.requestedByName ? `, by ${open.requestedByName}` : ''}.
              {isActive && ' Your current plan keeps working until it is decided.'}
            </span>
            {isAdmin && (
              <Button variant="secondary" size="sm" onClick={withdraw} disabled={busy}>
                Withdraw
              </Button>
            )}
          </div>
        </Card>
      )}

      <Card>
        <Card.Header
          title={isActive ? 'Change your plan' : 'Choose a plan'}
          subtitle={
            isActive
              ? 'Ask a platform administrator to move your firm to a different plan. Nothing is charged here.'
              : 'Your firm cannot use the platform until a plan is approved. Nothing is charged here.'
          }
        />

        <div className="pc-plans">
          {PLANS.map((plan) => {
            const isCurrent = subscription.plan === plan.id;
            const isPicked = selected === plan.id;
            return (
              <div
                key={plan.id}
                className={`pc-plan${isPicked && pickable ? ' is-picked' : ''}${isCurrent ? ' is-current' : ''}`}
                onClick={() => pickable && pickPlan(plan.id)}
                role={pickable ? 'button' : undefined}
                tabIndex={pickable ? 0 : undefined}
                onKeyDown={(e) => {
                  if (pickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    pickPlan(plan.id);
                  }
                }}
              >
                <div className="pc-plan-head">
                  <h4>{plan.name}</h4>
                  {isCurrent && <span className="pc-chip pc-chip--current">Current</span>}
                  {!isCurrent && plan.id === suggested && (
                    <span className="pc-chip pc-chip--suggested">Fits {requestedSeats} users</span>
                  )}
                </div>
                <p className="pc-plan-tagline">{plan.tagline}</p>
                <div className="pc-plan-price">
                  <strong>{formatPrice(plan.priceMonthly)}</strong>
                  {/* "Free / month" reads like a discount on something. A price
                      of nothing is a tier, and the word alone says it. */}
                  {plan.priceMonthly > 0 && <span className="pc-muted"> / month</span>}
                </div>
                <div className="pc-plan-seats">{seatRangeLabel(plan.id)}</div>
                <ul className="pc-plan-features">
                  {plan.features.map((f) => <li key={f}>{f}</li>)}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Committing the firm to a plan is the Admin's call, not an
            associate's — everyone here can read the screen, only an Admin acts.
            The server enforces the same rule with a 403, so this only avoids
            offering a form that cannot be submitted. */}
        {!isAdmin ? (
          <p className="pc-muted">
            {isActive
              ? 'Only your firm\'s administrator can change the plan.'
              : 'Your firm\'s administrator needs to choose a plan before the rest of the platform opens.'}
          </p>
        ) : open ? null : (
          <div className="pc-request">
            <div className="pc-request-row">
              <Select label="Plan" value={selected || ''} onChange={(e) => pickPlan(e.target.value)}>
                {PLANS.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} — {seatRangeLabel(p.id)}</option>
                ))}
              </Select>
              <Input
                label="How many users do you need?"
                type="number"
                min="1"
                value={requestedSeats}
                onChange={(e) => setRequestedSeats(e.target.value)}
              />
            </div>
            <Input
              label="Anything to add (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Tell the reviewer about your firm"
            />
            {/* Said rather than silently corrected: a firm may have a reason to
                ask for more room than its headcount needs today. */}
            {suggested && suggested !== selected && (
              <p className="pc-hint">
                {requestedSeats} users falls in the <strong>{planName(suggested)}</strong> band.
                You can still ask for {planName(selected)} — the reviewer decides.
              </p>
            )}
            {/* The free plan is granted in the same call, but ONLY to a firm
                holding nothing — so an active firm gets the ordinary wording,
                because for them it really is a request somebody must rule on. */}
            {isSelfServe(selected) && !isActive && (
              <p className="pc-hint">
                The free plan starts immediately — no approval to wait for. You can ask to move
                to a paid plan whenever the firm outgrows it.
              </p>
            )}
            <div className="pc-actions">
              <Button variant="primary" onClick={submit} loading={busy} disabled={!selected}>
                {isSelfServe(selected) && !isActive
                  ? 'Start free'
                  : isActive
                    ? 'Request this plan'
                    : 'Request approval'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

export default PlanChooser;
